import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import {
  createImageMaskMotion,
  createLinesMotion,
  createMaskMotion,
  createWordsMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useImageTextMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const textScrollTrigger = useRef<ScrollTrigger>(null!)
  const imageScrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const titleMotion = useRef<Motion | null>(null)
  const pretitleMotion = useRef<Motion | null>(null)
  const paragraphMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    titleMotion.current?.revert()
    pretitleMotion.current?.revert()
    paragraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const title = selector?.('[data-motion-title]')[0]
        const pretitle = selector?.('[data-motion-pretitle]')[0]
        const paragraph = selector?.('[data-motion-paragraph]')[0]

        titleMotion.current = title ? createWordsMotion(title) : null
        pretitleMotion.current = pretitle ? createMaskMotion(pretitle) : null
        paragraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap
          .timeline({
            /* onComplete: revertSplitText */
          })
          .from('[data-motion-background]', {
            opacity: 0,
            scale: 1.2,
            duration: 1,
            ease: 'power3.out'
          })
        titleMotion.current && animation.add(titleMotion.current.timeline, 0)
        pretitleMotion.current && animation.add(pretitleMotion.current.timeline, 0.1)
        paragraphMotion.current && animation.add(paragraphMotion.current.timeline, 0.2)

        textScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: '[data-motion-text-col]',
            animation
          })
        })
      },

      revert() {
        textScrollTrigger.current?.kill()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer() {
        if (!lenis) return

        const animation = createImageMaskMotion('[data-motion-image]', '[data-motion-image-col]')
        imageScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: '[data-motion-image-col]',
            animation
          })
        })
      },

      revert() {
        imageScrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  return { motionRef }
}
